import assert from 'assert';

import { BigNumber, Contract, providers, Signer } from 'ethers';
import Contracts from '@tokenysolutions/t-rex';

import { contractFactory, OverrideTransactionsInterface } from '../utils/utils';

type TransactionResponse = providers.TransactionResponse;

/**
 * Deploy T-Rex Token on Ethereum Blockchain, needs to have IdentityRegistry and Compliance already deployed.
 * @param {{signer: Signer; overrides: OverrideTransactionsInterface}} contract
 * @returns {Promise<Contract>} Returns the instance of the deployed contract
 */
export async function deployTokenImplementation(contract: { signer: Signer; overrides: OverrideTransactionsInterface }): Promise<Contract> {
  const factory = contractFactory({
    abi: Contracts.contracts.Token.abi,
    bytecode: Contracts.contracts.Token.bytecode,
    signer: contract.signer,
  });
  return await factory.deploy(contract.overrides);
}

/**
 * Deploy implementationAuthority contract
 * @param {{tokenImplementation: string; signer: Signer; overrides: OverrideTransactionsInterface}} contract
 * @returns {Promise<Contract>} Returns the instance of the deployed contract
 */
export async function deployImplementationAuthority(contract: {
  tokenImplementation: string;
  signer: Signer;
  overrides: OverrideTransactionsInterface;
}): Promise<Contract> {
  const factory = contractFactory({
    abi: Contracts.contracts.ImplementationAuthority.abi,
    bytecode: Contracts.contracts.ImplementationAuthority.bytecode,
    signer: contract.signer,
  });
  return await factory.deploy(contract.tokenImplementation, contract.overrides);
}

/**
 * Deploy T-Rex Token on Ethereum Blockchain, needs to have IdentityRegistry and Compliance already deployed.
 * @param {{implementationAuthority: string; identityRegistry: string; compliance: string; name: string; symbol: string; decimals: string; onchainID: string; signer: Signer; overrides: OverrideTransactionsInterface}} contract
 * @returns {Promise<Contract>} Returns the instance of the deployed contract
 */
export async function deployToken(contract: {
  implementationAuthority: string;
  identityRegistry: string;
  compliance: string;
  name: string;
  symbol: string;
  decimals: number;
  onchainID: string;
  signer: Signer;
  overrides: OverrideTransactionsInterface;
}): Promise<Contract> {
  const factory = contractFactory({
    abi: Contracts.contracts.TokenProxy.abi,
    bytecode: Contracts.contracts.TokenProxy.bytecode,
    signer: contract.signer,
  });
  const tokenProxy = await factory.deploy(
    contract.implementationAuthority,
    contract.identityRegistry,
    contract.compliance,
    contract.name,
    contract.symbol,
    contract.decimals,
    contract.onchainID,
    contract.overrides,
  );

  return new Contract(tokenProxy.address, Contracts.contracts.Token.abi, contract.signer);
}

/**
 * Mints an amount of tokens to the address's wallet
 * @param {{address: string; amount: BigNumber; token: Contract; overrides: OverrideTransactionsInterface}} tx
 * @returns {Promise<TransactionResponse>}
 */
export async function mint(tx: {
  address: string;
  amount: BigNumber;
  token: Contract;
  overrides: OverrideTransactionsInterface;
}): Promise<TransactionResponse> {
  return await tx.token.mint(tx.address, tx.amount, tx.overrides);
}

/**
 * Mints amounts of tokens to multiple addresses
 * @returns {Promise<TransactionResponse>}
 * @param tx
 */
export async function batchMint(tx: {
  addresses: string[];
  amounts: BigNumber[];
  token: Contract;
  overrides: OverrideTransactionsInterface;
}): Promise<TransactionResponse> {
  return await tx.token.batchMint(tx.addresses, tx.amounts, tx.overrides);
}

/**
 * Burns an amount of tokens from the address's wallet
 * @param {{address: string; amount: BigNumber; token: Contract; signer: Signer; overrides: OverrideTransactionsInterface}} tx
 * @returns {Promise<TransactionResponse>}
 */
export async function burn(tx: {
  address: string;
  amount: BigNumber;
  token: Contract;
  signer: Signer;
  overrides: OverrideTransactionsInterface;
}): Promise<TransactionResponse> {
  assert(await tx.token.isAgent(await tx.signer.getAddress()), 'Signer must be agent');
  return await tx.token.burn(tx.address, tx.amount, tx.overrides);
}

/**
 * Burns amounts of tokens from multiple addresses
 * @returns {Promise<TransactionResponse>}
 * @param tx
 */
export async function batchBurn(tx: {
  addresses: string[];
  amounts: BigNumber[];
  token: Contract;
  overrides: OverrideTransactionsInterface;
}): Promise<TransactionResponse> {
  return await tx.token.batchBurn(tx.addresses, tx.amounts, tx.overrides);
}

/**
 * Freezes an address making it impossible to do operations like transfers
 * @param {{address: string; freeze: boolean; token: Contract; signer: Signer; overrides: OverrideTransactionsInterface}} tx
 * @returns {Promise<TransactionResponse>}
 */
export async function freezeAddress(tx: {
  address: string;
  freezeStatus: boolean;
  token: Contract;
  signer: Signer;
  overrides: OverrideTransactionsInterface;
}): Promise<TransactionResponse> {
  assert(await tx.token.isAgent(await tx.signer.getAddress()), 'Signer must be agent');
  return await tx.token.setAddressFrozen(tx.address, tx.freezeStatus, tx.overrides);
}

/**
 *  Freezes addresses in Batch
 * @returns {Promise<TransactionResponse>}
 * @param tx
 */
export async function batchFreezeAddresses(tx: {
  addresses: string[];
  freezeStatus: boolean[];
  token: Contract;
  signer: Signer;
  overrides: OverrideTransactionsInterface;
}): Promise<TransactionResponse> {
  assert(await tx.token.isAgent(await tx.signer.getAddress()), 'Signer must be agent');
  return await tx.token.batchSetAddressFrozen(tx.addresses, tx.freezeStatus, tx.overrides);
}

/**
 * Transfers an amount of tokens to the address's wallet.
 * @param {{to: string; amount: BigNumber; token: Contract; signer: Signer; overrides: OverrideTransactionsInterface}} tx
 * @returns {Promise<TransactionResponse>}
 */
export async function transfer(tx: {
  to: string;
  amount: BigNumber;
  token: Contract;
  signer: Signer;
  overrides: OverrideTransactionsInterface;
}): Promise<TransactionResponse> {
  return await tx.token.transfer(tx.to, tx.amount, tx.overrides);
}

/**
 * Transfers an amount of tokens to a list of addresses in Batch
 * @returns {Promise<TransactionResponse>}
 * @param tx
 */
export async function batchTransfer(tx: {
  toList: string[];
  amounts: BigNumber[];
  token: Contract;
  signer: Signer;
  overrides: OverrideTransactionsInterface;
}): Promise<TransactionResponse> {
  return await tx.token.batchTransfer(tx.toList, tx.amounts, tx.overrides);
}

/**
 * Transfer from an address to another
 * @param {{from: string; to: string; amount: BigNumber; token: Contract; signer: Signer; overrides: OverrideTransactionsInterface}} tx
 * @returns {Promise<TransactionResponse>}
 */
export async function transferFrom(tx: {
  from: string;
  to: string;
  amount: BigNumber;
  token: Contract;
  signer: Signer;
  overrides: OverrideTransactionsInterface;
}): Promise<TransactionResponse> {
  return await tx.token.transferFrom(tx.from, tx.to, tx.amount, tx.overrides);
}

/**
 * Sets 'amount' as the allowance of 'spender' over the caller's token
 * @param tx
 */
export async function approve(tx: {
  address: string;
  amount: BigNumber;
  token: Contract;
  signer: Signer;
  overrides: OverrideTransactionsInterface;
}): Promise<TransactionResponse> {
  return await tx.token.approve(tx.address, tx.amount, tx.overrides);
}

/**
 * Increases the allowance granted to 'spender' by the caller
 * @param tx
 */
export async function increaseAllowance(tx: {
  address: string;
  amount: BigNumber;
  token: Contract;
  signer: Signer;
  overrides: OverrideTransactionsInterface;
}): Promise<TransactionResponse> {
  return await tx.token.increaseAllowance(tx.address, tx.amount, tx.overrides);
}

/**
 * Decreases the allowance granted to 'spender' by the caller
 * @param tx
 */
export async function decreaseAllowance(tx: {
  address: string;
  amount: BigNumber;
  token: Contract;
  signer: Signer;
  overrides: OverrideTransactionsInterface;
}): Promise<TransactionResponse> {
  return await tx.token.decreaseAllowance(tx.address, tx.amount, tx.overrides);
}

/**
 * Enforces the execution of a transfer between two addresses.
 * @param {{from: string; to: string; amount: BigNumber; token: Contract; signer: Signer; overrides: OverrideTransactionsInterface}} tx
 * @returns {Promise<TransactionResponse>}
 */
export async function forceTransfer(tx: {
  from: string;
  to: string;
  amount: BigNumber;
  token: Contract;
  signer: Signer;
  overrides: OverrideTransactionsInterface;
}): Promise<TransactionResponse> {
  assert(await tx.token.isAgent(await tx.signer.getAddress()), 'Need a signer');
  return await tx.token.forcedTransfer(tx.from, tx.to, tx.amount, tx.overrides);
}

/**
 * Returns the amount of tokens that 'spender' is allowed to spend on behalf the 'owner'
 * @param tx
 */
export async function getAllowance(tx: {
  owner: string;
  spender: string;
  token: Contract;
  signer: Signer;
  overrides: OverrideTransactionsInterface;
}): Promise<BigNumber> {
  return await tx.token.allowance(tx.owner, tx.spender, tx.overrides);
}

/**
 * Enforces the execution of a transfer between two addresses in Batch.
 * @returns {Promise<TransactionResponse>}
 * @param tx
 */
export async function batchForceTransfer(tx: {
  fromList: string[];
  toList: string[];
  amounts: BigNumber[];
  token: Contract;
  signer: Signer;
  overrides: OverrideTransactionsInterface;
}): Promise<TransactionResponse> {
  return await tx.token.batchForcedTransfer(tx.fromList, tx.toList, tx.amounts, tx.overrides);
}

/**
 * Freezes a portion of tokens held by the address's wallet
 * @param {{address: string; amount: BigNumber; token: Contract; signer: Signer; overrides: OverrideTransactionsInterface}} tx
 * @returns {Promise<TransactionResponse>}
 */
export async function freezePartialTokens(tx: {
  address: string;
  amount: BigNumber;
  token: Contract;
  signer: Signer;
  overrides: OverrideTransactionsInterface;
}): Promise<TransactionResponse> {
  assert(await tx.token.isAgent(await tx.signer.getAddress()), 'Signer must be agent');
  return await tx.token.freezePartialTokens(tx.address, tx.amount, tx.overrides);
}

/**
 * BatchFreezes a portion of tokens held by a list of shareholders
 * @returns {Promise<TransactionResponse>}
 * @param tx
 */
export async function batchFreezePartialTokens(tx: {
  addresses: string[];
  amounts: BigNumber[];
  token: Contract;
  signer: Signer;
  overrides: OverrideTransactionsInterface;
}): Promise<TransactionResponse> {
  assert(await tx.token.isAgent(await tx.signer.getAddress()), 'Signer must be agent');
  return await tx.token.batchFreezePartialTokens(tx.addresses, tx.amounts, tx.overrides);
}

/**
 * Unfreezes a portion of tokens held by the address's wallet
 * @param {{address: string; amount: BigNumber; token: Contract; signer: Signer; overrides: OverrideTransactionsInterface}} tx
 * @returns {Promise<TransactionResponse>}
 */
export async function unfreezePartialTokens(tx: {
  address: string;
  amount: BigNumber;
  token: Contract;
  signer: Signer;
  overrides: OverrideTransactionsInterface;
}): Promise<TransactionResponse> {
  assert(await tx.token.isAgent(await tx.signer.getAddress()), 'Signer must be agent');
  return await tx.token.unfreezePartialTokens(tx.address, tx.amount, tx.overrides);
}

/**
 * BatchUnfreezes a portion of tokens held by a list of shareholders
 * @returns {Promise<TransactionResponse>}
 * @param tx
 */
export async function batchUnfreezePartialTokens(tx: {
  addresses: string[];
  amounts: BigNumber[];
  token: Contract;
  signer: Signer;
  overrides: OverrideTransactionsInterface;
}): Promise<TransactionResponse> {
  assert(await tx.token.isAgent(await tx.signer.getAddress()), 'Signer must be agent');
  return await tx.token.batchUnfreezePartialTokens(tx.addresses, tx.amounts, tx.overrides);
}

/**
 * @dev recovery function used to force transfer tokens from a lost wallet to a new wallet for an investor.
 * This function can only be called by a wallet set as agent of the token
 * @param tx
 */
export async function recoveryAddress(tx: {
  lostWallet: string;
  newWallet: string;
  onchainID: string;
  token: Contract;
  signer: Signer;
  overrides: OverrideTransactionsInterface;
}): Promise<TransactionResponse> {
  return await tx.token.recoveryAddress(tx.lostWallet, tx.newWallet, tx.onchainID, tx.overrides);
}

/**
 * Pause Token smart contract, rendering execution of operations impossible.
 * @param {{token: Contract; signer: Signer; overrides: OverrideTransactionsInterface}} tx
 * @returns {Promise<TransactionResponse>}
 */
export async function pauseToken(tx: { token: Contract; signer: Signer; overrides: OverrideTransactionsInterface }): Promise<TransactionResponse> {
  assert(await tx.token.isAgent(await tx.signer.getAddress()), 'Signer must be agent');
  return await tx.token.pause(tx.overrides);
}

/**
 * Unpause Token smart contract, removes all restrictions imposed previously.
 * @param {{token: Contract; signer: Signer; overrides: OverrideTransactionsInterface}} tx
 * @returns {Promise<TransactionResponse>}
 */
export async function unpauseToken(tx: { token: Contract; signer: Signer; overrides: OverrideTransactionsInterface }): Promise<TransactionResponse> {
  assert(await tx.token.isAgent(await tx.signer.getAddress()), 'Signer must be agent');
  return await tx.token.unpause(tx.overrides);
}

/**
 * Adds an Agent for the Token contract
 * @param {{agent: string; token: Contract; signer: Signer; overrides: OverrideTransactionsInterface}} tx
 * @returns {Promise<TransactionResponse>}
 */
export async function addAgentToToken(tx: {
  agent: string;
  token: Contract;
  signer: Signer;
  overrides: OverrideTransactionsInterface;
}): Promise<TransactionResponse> {
  return await tx.token.addAgentOnTokenContract(tx.agent, tx.overrides);
}

/**
 * Removes an Agent from the Token contract
 * @param {{agent: string; token: Contract; signer: Signer; overrides: OverrideTransactionsInterface}} tx
 * @returns {Promise<TransactionResponse>}
 */
export async function removeAgentFromToken(tx: {
  agent: string;
  token: Contract;
  signer: Signer;
  overrides: OverrideTransactionsInterface;
}): Promise<TransactionResponse> {
  return await tx.token.removeAgentOnTokenContract(tx.agent, tx.overrides);
}

// Setters

/**
 * Changes token's name
 * /!\ Use it carefully, could lead to undefined behavior on Etherscan.
 * @param {{name: string; token: Contract; overrides: OverrideTransactionsInterface}} tx
 * @returns {Promise<TransactionResponse>}
 */
export async function setName(tx: { name: string; token: Contract; overrides: OverrideTransactionsInterface }): Promise<TransactionResponse> {
  return await tx.token.setName(tx.name, tx.overrides);
}

/**
 * Changes token's symbol
 * /!\ Use it carefully, could lead to undefined behavior on Etherscan.
 * @param {{symbol: string; token: Contract; overrides: OverrideTransactionsInterface}} tx
 * @returns {Promise<TransactionResponse>}
 */
export async function setSymbol(tx: { symbol: string; token: Contract; overrides: OverrideTransactionsInterface }): Promise<TransactionResponse> {
  return await tx.token.setSymbol(tx.symbol, tx.overrides);
}

/**
 * Changes token's OnchainID
 * /!\ Use it carefully, could lead to undefined behavior on Etherscan.
 * @param {{onchainID: string; token: Contract; overrides: OverrideTransactionsInterface}} tx
 * @returns {Promise<TransactionResponse>}
 */
export async function setOnchainID(tx: { onchainID: string; token: Contract; overrides: OverrideTransactionsInterface }): Promise<TransactionResponse> {
  return await tx.token.setOnchainID(tx.onchainID, tx.overrides);
}

/**
 * Returns the address of the IdentityRegistry linked to this contract
 * @param {{token: Contract}} tx
 * @returns {Promise<Contract>}
 */
export async function getIdentityRegistry(tx: { token: Contract }): Promise<Contract> {
  return await tx.token.identityRegistry();
}

/**
 * Returns the address of the Compliance linked to this contract
 * @param {{token: Contract}} tx
 * @returns {Promise<Contract>}
 */
export async function getCompliance(tx: { token: Contract }): Promise<Contract> {
  return await tx.token.compliance();
}

/**
 * Changes token's IdentityRegistry Contract
 * @param {{address: string; token: Contract; overrides: OverrideTransactionsInterface}} tx
 * @returns {Promise<TransactionResponse>}
 */
export async function setIdentityRegistry(tx: { address: string; token: Contract; overrides: OverrideTransactionsInterface }): Promise<TransactionResponse> {
  return await tx.token.setIdentityRegistry(tx.address, tx.overrides);
}

/**
 * Changes token's Compliance Contract
 * @param {{address: string; token: Contract; overrides: OverrideTransactionsInterface}} tx
 * @returns {Promise<TransactionResponse>}
 */
export async function setCompliance(tx: { address: string; token: Contract; overrides: OverrideTransactionsInterface }): Promise<TransactionResponse> {
  return await tx.token.setCompliance(tx.address, tx.overrides);
}

// Getters

/**
 * Returns token's name
 * @param {{token: Contract}} contract
 * @returns {Promise<string>}
 */
export async function getName(contract: { token: Contract }): Promise<string> {
  return await contract.token.name();
}

/**
 * Returns token's symbol
 * @param {{token: Contract}} contract
 * @returns {Promise<string>}
 */
export async function getSymbol(contract: { token: Contract }): Promise<string> {
  return await contract.token.symbol();
}

/**
 * Returns token's version
 * @param {{token: Contract}} contract
 * @returns {Promise<string>}
 */
export async function getVersion(contract: { token: Contract }): Promise<string> {
  return await contract.token.version();
}

/**
 * Returns token's decimals
 * @param {{token: Contract}} contract
 * @returns {Promise<number>}
 */
export async function getDecimals(contract: { token: Contract }): Promise<number> {
  return await contract.token.decimals();
}

/**
 * Returns token's onchainID address
 * @param {{token: Contract}} contract
 * @returns {Promise<string>}
 */
export async function getOnchainID(contract: { token: Contract }): Promise<string> {
  return await contract.token.onchainID();
}

/**
 * Returns address's balance
 * @param {{address: string; token: Contract}} contract
 * @returns {Promise<BigNumber>}
 */
export async function getBalance(contract: { address: string; token: Contract }): Promise<BigNumber> {
  return await contract.token.balanceOf(contract.address);
}

/**
 *  Returns address's freeze status
 * @param {{address: string; token: Contract}} tx
 * @returns {Promise<TransactionResponse>}
 */
export async function isFrozen(tx: { address: string; token: Contract }): Promise<boolean> {
  return await tx.token.isFrozen(tx.address);
}

/**
 * Returns the amount of frozen tokens of held by the address's wallet.
 * @param {{address: string; token: Contract}} contract
 * @returns {Promise<BigNumber>}
 */
export async function getFrozenAmount(contract: { address: string; token: Contract }): Promise<BigNumber> {
  return await contract.token.getFrozenTokens(contract.address);
}

/**
 * Returns Token's Pause status
 * @param {{token: Contract}} contract
 * @returns {Promise<boolean>}
 */
export async function getPauseStatus(contract: { token: Contract }): Promise<boolean> {
  return await contract.token.paused();
}

/**
 * Returns the Owner of the Token Contract.
 * @param {{token: Contract; overrides: OverrideTransactionsInterface}} tx
 * @returns {Promise<TransactionResponse>}
 */
export async function getOwnerOfToken(tx: { token: Contract }): Promise<string> {
  return await tx.token.owner();
}

/**
 * Returns the Owner of the ImplementationAuthority Contract.
 * @param {{implementationAuthority: Contract; overrides: OverrideTransactionsInterface}} tx
 * @returns {Promise<TransactionResponse>}
 */
export async function getOwnerOfImplementationAuthority(tx: { implementationAuthority: Contract }): Promise<string> {
  return await tx.implementationAuthority.owner();
}

/**
 * Transfers the Ownership of the Identity Registry to a new Owner.
 * @param {{newOwner: string; registry: Contract; overrides: OverrideTransactionsInterface}} tx
 * @returns {Promise<TransactionResponse>}
 */
export async function transferTokenOwnership(tx: {
  newOwner: string;
  token: Contract;
  overrides: OverrideTransactionsInterface;
}): Promise<TransactionResponse> {
  return await tx.token.transferOwnershipOnTokenContract(tx.newOwner, tx.overrides);
}
